/** Remastered explosions, keys, doors and the clock. Pure. */

import { KEY_COLOURS, rgb } from '../palette';
import type { RampSet, RGB } from './color';
import { rampOf } from './color';
import { Px } from './px';
import { hash3, tileNoise } from './shade';

const HOT: RGB = [255, 252, 236];

/** Stage 0-4: a white-hot flash, a fireball, then smoke breaking up. */
export function explosion(r: RampSet, stage: number): Px {
  const p = new Px();
  const radius = [4.5, 6.8, 8.4, 8.8, 8.4][stage] ?? 8;
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const d = Math.hypot(x + 0.5 - 8, y + 0.5 - 8);
      const v = 1 - d / radius + (tileNoise(x + stage * 5, y, 3, 16, 70 + stage) - 0.5) * 0.7;
      if (v <= 0) continue;
      if (stage >= 3) {
        if (v < 0.25 || hash3(x, y, stage) % 5 === 0) continue;
        p.set(x, y, v > 0.55 ? r.rock[2] : r.rock[1]);
      } else if (v > 0.62) p.set(x, y, stage === 0 ? HOT : r.bright[4]);
      else if (v > 0.38) p.set(x, y, r.bright[3]);
      else if (v > 0.18) p.set(x, y, r.earth[4]);
      else p.set(x, y, r.earth[3]);
    }
  }
  return p;
}

function keyRamp(color: number): RampSet {
  const c = KEY_COLOURS[color] ?? 7;
  const ramp = rampOf(rgb(c));
  return { earth: ramp, rock: ramp, bright: rampOf(rgb(1)), ink: [16, 10, 24] };
}

/** A key in its own colour: a ring, a shaft and two teeth. */
export function key(color: number): Px {
  const k = keyRamp(color);
  const p = new Px();
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const d = Math.hypot(x + 0.5 - 8, y + 0.5 - 5);
      if (d <= 3.6 && d >= 1.6) p.set(x, y, k.rock[x + y < 11 ? 4 : x + y < 14 ? 3 : 1] ?? k.rock[2]);
    }
  }
  p.bar(7, 8, 8, 14, k.rock, 1, 4);
  p.fill(9, 11, 10, 11, k.rock[2]);
  p.fill(9, 13, 11, 13, k.rock[2]);
  p.outline(k.ink);
  return p;
}

/** A locked door in its key's colour: a bevelled slab with panels and a keyhole. */
export function lockedDoor(color: number): Px {
  const k = keyRamp(color);
  const p = new Px();
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      let s = 2;
      if (x === 15 || y === 15) s = 0;
      else if (x === 0 || y === 0) s = 4;
      else if (x === 14 || y === 14) s = 1;
      else if ((y === 3 || y === 11) && x > 2 && x < 13) s = 1;
      else if ((y === 4 || y === 12) && x > 2 && x < 13) s = 3;
      p.set(x, y, k.rock[s] ?? k.rock[2]);
    }
  }
  p.fill(7, 6, 8, 7, k.ink);
  p.set(7, 8, k.ink);
  p.set(8, 8, k.ink);
  p.fill(7, 9, 8, 9, k.ink);
  return p;
}

/** The last glimpse of a door as it swings away: its frame round darkness. */
export function openingDoor(r: RampSet): Px {
  const p = new Px();
  for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) if (x < 2 || y < 2 || x > 13) p.set(x, y, r.rock[x < 2 || y < 2 ? 3 : 1]);
  return p;
}

/** An hourglass: frame A full at the top, B running down. */
export function clock(r: RampSet, frame: number): Px {
  const p = new Px();
  p.bar(3, 1, 12, 2, r.earth, 1, 4);
  p.bar(3, 13, 12, 14, r.earth, 1, 4);
  for (let y = 3; y <= 12; y++) {
    const half = 1 + Math.round(Math.abs(y - 7.5) * 0.9);
    for (let x = 8 - half; x < 8 + half; x++) {
      const edge = x === 8 - half || x === 7 + half;
      const top = y < 8;
      const sand = frame === 0 ? top && y >= 4 : !top ? y >= 10 : false;
      p.set(x, y, edge ? r.rock[3] : sand ? r.bright[x < 8 ? 4 : 3] : [18, 16, 34]);
    }
  }
  if (frame === 1) for (let y = 7; y <= 11; y++) p.set(7, y, r.bright[4]);
  p.outline(r.ink);
  return p;
}
